/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  AssignmentOutlined,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ThumbUpOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import colors from '../colors.styles';
import adminFormStyles from '../shared/styles/adminForm.styles';
import {
  getRequestSubtitleParts,
  getTaskStatus,
  TASK_STATUS,
} from './Tasks.service';

const STATUS_CHIP = {
  [TASK_STATUS.ACTIVE]: {
    label: 'Active',
    background: colors.base.light_green,
    color: colors.base.dark_green,
  },
  [TASK_STATUS.COMPLETE]: {
    label: 'Complete',
    background: colors.base.very_light_gray,
    color: colors.base.dark_gray,
  },
};

const SUBMISSIONS_PLACEHOLDER = 'Submissions will show here.';

const styles = {
  paper: {
    ...adminFormStyles.section,
    backgroundColor: colors.base.light,
    overflow: 'hidden',
  },
  header: {
    alignItems: 'flex-start',
    cursor: 'pointer',
    display: 'flex',
    gap: 1.5,
    px: 2,
    py: 2,
  },
  chevron: {
    color: '#656d80',
    mt: '2px',
  },
  statusDot: {
    backgroundColor: colors.base.dark_green,
    borderRadius: '50%',
    display: 'inline-block',
    height: 8,
    mr: 1,
    verticalAlign: 'middle',
    width: 8,
  },
  // Dot, title, and chip share one text flow so a long title wraps at a word
  // boundary instead of dropping below the dot as a flex item.
  title: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 800,
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  chip: {
    fontSize: '12px',
    fontWeight: 700,
    height: 24,
    ml: 1,
    verticalAlign: 'middle',
  },
  subtitle: {
    alignItems: 'center',
    color: '#656d80',
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: '14px',
    gap: 0.75,
    mt: 0.5,
  },
  likedGroup: {
    alignItems: 'center',
    display: 'flex',
    gap: 0.5,
  },
  likedIcon: {
    fontSize: 16,
  },
  detailsButton: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
    flexShrink: 0,
    ml: 'auto',
  },
  body: {
    borderTop: '1px solid #e9ecee',
    px: 2,
    py: 1.5,
  },
  itemHeader: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: 1,
    py: 1,
  },
  itemTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    fontWeight: 700,
  },
  placeholder: {
    color: '#656d80',
    fontSize: '14px',
    px: 4,
    py: 1.5,
  },
};

const contentRequestShape = PropTypes.shape({
  id: PropTypes.number,
  ui_id: PropTypes.string,
  title: PropTypes.string,
});

const taskShape = PropTypes.shape({
  task_id: PropTypes.number.isRequired,
  title: PropTypes.string,
  launch_date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  due_date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  submission_count: PropTypes.number,
  liked_count: PropTypes.number,
  content_requests: PropTypes.arrayOf(contentRequestShape),
});

function ExpandChevron({ expanded, label }) {
  return (
    <IconButton
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
      size="small"
      sx={styles.chevron}
    >
      {expanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
}

ExpandChevron.propTypes = {
  expanded: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

function ContentRequestItem({ contentRequest }) {
  const [expanded, setExpanded] = useState(false);
  const title = contentRequest.title || 'Untitled item';

  return (
    <Box>
      <Box
        onClick={() => setExpanded((current) => !current)}
        sx={styles.itemHeader}
      >
        <ExpandChevron expanded={expanded} label={title} />
        <Typography sx={styles.itemTitle}>{title}</Typography>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        {/* Placeholder for WMTA-9958 — viewing submissions */}
        <Typography sx={styles.placeholder}>
          {SUBMISSIONS_PLACEHOLDER}
        </Typography>
      </Collapse>
    </Box>
  );
}

ContentRequestItem.propTypes = {
  contentRequest: contentRequestShape.isRequired,
};

export default function ContentRequestBar({ task }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const status = getTaskStatus(task);
  const chip = STATUS_CHIP[status];
  const title = task.title || 'Untitled request';
  const subtitle = getRequestSubtitleParts(task);
  const contentRequests = task.content_requests || [];

  function openDetails(event) {
    event.stopPropagation();
    navigate(`/tasks/${task.task_id}`);
  }

  return (
    <Paper sx={styles.paper} variant="outlined">
      <Box
        onClick={() => setExpanded((current) => !current)}
        sx={styles.header}
      >
        <ExpandChevron expanded={expanded} label={title} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={styles.title}>
            {status === TASK_STATUS.ACTIVE && (
              <Box component="span" sx={styles.statusDot} />
            )}
            {title}
            {chip && (
              <Chip
                label={chip.label}
                size="small"
                sx={{
                  ...styles.chip,
                  backgroundColor: chip.background,
                  color: chip.color,
                }}
              />
            )}
          </Typography>
          <Typography component="div" sx={styles.subtitle}>
            <span>{subtitle.submissions}</span>
            <span>·</span>
            <Box sx={styles.likedGroup}>
              <ThumbUpOutlined
                aria-label="Liked submissions"
                sx={styles.likedIcon}
              />
              {subtitle.liked}
            </Box>
            {subtitle.due && (
              <>
                <span>·</span>
                <span>{subtitle.due}</span>
              </>
            )}
          </Typography>
        </Box>
        <Button
          onClick={openDetails}
          startIcon={<AssignmentOutlined />}
          sx={styles.detailsButton}
          variant="outlined"
        >
          View request details
        </Button>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={styles.body}>
          {contentRequests.length === 0 ? (
            <Typography sx={styles.placeholder}>
              {SUBMISSIONS_PLACEHOLDER}
            </Typography>
          ) : (
            contentRequests.map((contentRequest) => (
              <ContentRequestItem
                key={contentRequest.ui_id || contentRequest.id}
                contentRequest={contentRequest}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

ContentRequestBar.propTypes = {
  task: taskShape.isRequired,
};
